import type {
  Conversation,
  ConversationUpdate,
  NewConversation,
} from "@/conversations/types";
import { conversationsGateway } from "@/gateways/conversationsGateway";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { ref } from "vue";

export function useNewConversation(houseId: number, canvassId: string) {
  const conversation = ref<NewConversation>({
    id: globalThis.crypto.randomUUID(),
    content: "",
    houseId,
    canvassId,
    contactId: null,
    satisfiedCanvassTargetIds: [],
  });

  return { conversation };
}

export function useGetConversation(id: string) {
  const query = useQuery({
    queryKey: ["conversation", id],
    queryFn: () => conversationsGateway.getConversation(id),
  });

  return {
    ...query,
    getConversationStatus: query.status,
    conversation: query.data,
  };
}

export function useGetConversations(houseId: number) {
  const query = useQuery({
    queryKey: ["conversations", houseId],
    queryFn: () => conversationsGateway.listConversationsByHouseId(houseId),
  });

  return {
    ...query,
    getConversationsStatus: query.status,
    conversations: query.data,
  };
}

export function useCreateConversation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (conversation: NewConversation) =>
      conversationsGateway.createConversation(conversation),
    onSuccess: (conversation: Conversation) => {
      queryClient.invalidateQueries({
        queryKey: ["conversations", conversation.houseId],
      });
    },
  });

  return {
    ...mutation,
    createConversationStatus: mutation.status,
    createConversation: mutation.mutate,
  };
}

export function useUpdateConversation(houseId: number) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (conversation: ConversationUpdate) =>
      conversationsGateway.updateConversation(conversation),
    onSuccess: (conversation: Conversation) => {
      queryClient.invalidateQueries({ queryKey: ["conversations", houseId] });
      queryClient.invalidateQueries({
        queryKey: ["conversation", conversation.id],
      });
    },
  });

  return {
    ...mutation,
    updateConversation: mutation.mutate,
  };
}

export function useDeleteConversation(houseId: number) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (conversationId: string) =>
      conversationsGateway.deleteConversation(conversationId),
    onSuccess: (_data, conversationId) => {
      queryClient.invalidateQueries({ queryKey: ["conversations", houseId] });
      queryClient.removeQueries({ queryKey: ["conversation", conversationId] });
    },
  });

  return {
    ...mutation,
    resetDeleteConversation: mutation.reset,
    deleteConversationStatus: mutation.status,
    deleteConversation: mutation.mutate,
  };
}
